// The owners' daily summary (N-27, ADR-0044; spec/16 §8, spec/17 §4.5; F-ANL-07): the maths that is the summary's own —
// when a summary is due, how its figures add up across venues, how a figure compares with last week and the trailing
// average, and which flags an owner sees. Pure, like everything here: time is a parameter.
//
// **No money is derived here a second time.** Every money figure a summary carries is read by the server from the
// ledger through the canonical definitions (M-01 `revenueOf`, M-02 `netSalesOf`, M-03 `taxCollectedOf`, the day
// close's `dayCloseFiguresOf`, `playRevenueOf`) — this file only sums those per-venue figures into the organisation's
// (the ledger is additive, so a sum of venues is the organisation), averages them over a week (half up, once, to the
// minor unit) and compares them. Counts of players are unioned, never summed: a player who played at two venues is one
// player (M-06).
//
// ## When a summary is due (ADR-0044 §3)
//
//   venue          the venue's business day closed — by staff (F-OPS-06) or at the automatic rollover (ADR-0029) — or,
//                  if neither has happened, the rollover plus `DAILY_SUMMARY_GRACE_MS`, so it never waits forever.
//                  Whichever is first.
//   organisation   every live venue has closed its own business day of that date — or the latest venue's rollover
//                  plus the grace, whichever is first; a venue not closed by then is reported "day not closed yet".
//
// Each venue's business day of a date is its own (its timezone and cutoff): a date is grouped by each venue's own day.
import { roundHalfUpBigInt } from "../money/round.js";
import { REVENUE_TENDERS } from "./ledger-metrics.js";
/** How long after a business day's rollover a summary waits for the day to be closed before it goes anyway. */
export const DAILY_SUMMARY_GRACE_MS = 2 * 60 * 60 * 1000;
/** How far back a due summary is still sent (a worker that was down catches up); older ones are only read in the console. */
export const DAILY_SUMMARY_CATCH_UP_MS = 36 * 60 * 60 * 1000;
/** The comparison window: the same weekday a week before, and the seven business days before. */
export const DAILY_SUMMARY_TRAILING_DAYS = 7;
/** A station offline for at least this long during a session is flagged. */
export const STATION_OFFLINE_LONG_MS = 30 * 60 * 1000;
/** Refunds are flagged as high at this share of the day's payments (basis points: 10 %) … */
export const REFUNDS_HIGH_SHARE_BPS = 1000n;
/** … or at this multiple of the trailing seven-day average. */
export const REFUNDS_HIGH_MULTIPLE = 2n;
/** How many items the summary lists. */
export const DAILY_SUMMARY_TOP_ITEM_COUNT = 3;
/** The moment the grace runs out for a day that rolls over at `rolloverAt`. */
export function summaryDeadlineOf(rolloverAt) {
    return rolloverAt + DAILY_SUMMARY_GRACE_MS;
}
/** A venue's summary: due at its close, or at its rollover plus the grace — whichever is first (ADR-0044 §3). */
export function venueSummaryDue(state, now) {
    return organizationSummaryDue([state], now);
}
/**
 * The organisation's summary for one date: due once every live venue has closed its own business day of that date, or
 * at the latest venue's rollover plus the grace, whichever is first. With no venue there is nothing to summarise.
 */
export function organizationSummaryDue(states, now) {
    if (states.length === 0)
        return { due: false };
    const deadline = summaryDeadlineOf(Math.max(...states.map((state) => state.rolloverAt)));
    const allClosed = states.every((state) => state.closedAt !== null);
    const lastClose = allClosed ? Math.max(...states.map((state) => state.closedAt ?? 0)) : Number.POSITIVE_INFINITY;
    const at = Math.min(lastClose, deadline);
    if (now < at)
        return { due: false };
    const trigger = lastClose <= deadline ? 'closed' : 'deadline';
    const notClosed = states.filter((state) => state.closedAt === null || state.closedAt > at).map((state) => state.venueId);
    return { due: true, trigger, at, notClosed };
}
/** Whether a summary that became due at `dueAt` is still sent at `now` (a late one is read in the console only). */
export function stillSendable(dueAt, now) {
    return now - dueAt <= DAILY_SUMMARY_CATCH_UP_MS;
}
/** The subscribers a summary that became due at `dueAt` goes to: those subscribed before it was due (ADR-0044 §2). */
export function summaryRecipientsOf(subscribers, dueAt) {
    return subscribers.filter((subscriber) => subscriber.since <= dueAt);
}
/** How many times a summary's email is handed to the delivery pipeline before it is given up: the first and four retries. */
export const DAILY_SUMMARY_EMAIL_ATTEMPTS = 5;
/** The wait after the first failed attempt; each later one triples it (5, 15, 45, 135 minutes). */
export const DAILY_SUMMARY_RETRY_BASE_MS = 5 * 60 * 1000;
/**
 * Whether a summary's failed email is due another attempt: fewer than `DAILY_SUMMARY_EMAIL_ATTEMPTS` so far, and the
 * wait after the last one — `DAILY_SUMMARY_RETRY_BASE_MS × 3^(attempts − 1)` — has passed.
 */
export function emailRetryDue(attempts, lastAttemptAt, now) {
    if (attempts >= DAILY_SUMMARY_EMAIL_ATTEMPTS)
        return false;
    return now - lastAttemptAt >= DAILY_SUMMARY_RETRY_BASE_MS * 3 ** Math.max(0, attempts - 1);
}
const zeroTenders = () => ({ cash: 0n, counter_card: 0n, counter_upi: 0n, online: 0n, wallet: 0n });
/** No figures: an organisation with no venue, or a sum of nothing. */
export function emptyDaySummaryFigures() {
    return {
        grossSales: 0n,
        discounts: 0n,
        refunds: 0n,
        refundCount: 0,
        payments: 0n,
        tax: 0n,
        netTakings: 0n,
        moneyIn: 0n,
        byTender: zeroTenders(),
        onAccount: 0n,
        sessions: 0,
        playSeconds: 0,
        inSessionSeconds: 0,
        availableSeconds: 0,
        stations: 0,
        passSales: 0n,
        payAsYouGo: 0n,
        foodSales: 0n,
        retailSales: 0n,
        orders: 0,
        items: [],
        stockAlerts: null,
        walletTopUps: 0n,
        bonusIssued: 0n,
        walletSpent: 0n,
        cashExpected: null,
        cashCounted: null,
        cashVariance: 0n,
        openShifts: 0,
        players: new Set(),
        newPlayers: new Set(),
    };
}
const addNullable = (a, b) => (a === null ? b : b === null ? a : a + b);
const addNullableCount = (a, b) => (a === null ? b : b === null ? a : a + b);
/**
 * The organisation's figures: the venues' summed (the ledger is additive), items merged by kind and name, players
 * unioned (one person is one player however many venues they played at, M-06/M-07). Every part must be in one
 * currency — the caller sums a currency's venues only.
 */
export function sumDaySummaryFigures(parts) {
    let sum = emptyDaySummaryFigures();
    for (const part of parts) {
        const items = new Map();
        for (const item of [...sum.items, ...part.items]) {
            const key = `${item.kind}\u0000${item.name}`;
            const known = items.get(key);
            items.set(key, known === undefined ? item : { ...known, quantity: known.quantity + item.quantity, net: known.net + item.net });
        }
        const byTender = zeroTenders();
        for (const tender of REVENUE_TENDERS)
            byTender[tender] = sum.byTender[tender] + part.byTender[tender];
        sum = {
            grossSales: sum.grossSales + part.grossSales,
            discounts: sum.discounts + part.discounts,
            refunds: sum.refunds + part.refunds,
            refundCount: sum.refundCount + part.refundCount,
            payments: sum.payments + part.payments,
            tax: sum.tax + part.tax,
            netTakings: sum.netTakings + part.netTakings,
            moneyIn: sum.moneyIn + part.moneyIn,
            byTender,
            onAccount: sum.onAccount + part.onAccount,
            sessions: sum.sessions + part.sessions,
            playSeconds: sum.playSeconds + part.playSeconds,
            inSessionSeconds: sum.inSessionSeconds + part.inSessionSeconds,
            availableSeconds: sum.availableSeconds + part.availableSeconds,
            stations: sum.stations + part.stations,
            passSales: sum.passSales + part.passSales,
            payAsYouGo: sum.payAsYouGo + part.payAsYouGo,
            foodSales: sum.foodSales + part.foodSales,
            retailSales: sum.retailSales + part.retailSales,
            orders: sum.orders + part.orders,
            items: [...items.values()],
            stockAlerts: addNullableCount(sum.stockAlerts, part.stockAlerts),
            walletTopUps: sum.walletTopUps + part.walletTopUps,
            bonusIssued: sum.bonusIssued + part.bonusIssued,
            walletSpent: sum.walletSpent + part.walletSpent,
            cashExpected: addNullable(sum.cashExpected, part.cashExpected),
            cashCounted: addNullable(sum.cashCounted, part.cashCounted),
            cashVariance: sum.cashVariance + part.cashVariance,
            openShifts: sum.openShifts + part.openShifts,
            players: new Set([...sum.players, ...part.players]),
            newPlayers: new Set([...sum.newPlayers, ...part.newPlayers]),
        };
    }
    return sum;
}
/** The day's best sellers: most net sales first, then most sold, then by name — the Products tab's order. */
export function topItemsOf(items, count = DAILY_SUMMARY_TOP_ITEM_COUNT) {
    return [...items]
        .sort((a, b) => (b.net > a.net ? 1 : b.net < a.net ? -1 : b.quantity - a.quantity || a.name.localeCompare(b.name)))
        .slice(0, count);
}
export function daySummaryDerivedOf(figures) {
    return {
        averageSessionSeconds: figures.sessions > 0 ? Number(roundHalfUpBigInt(BigInt(figures.playSeconds), BigInt(figures.sessions))) : null,
        utilisationBasisPoints: basisPointsOf(figures.inSessionSeconds, figures.availableSeconds),
        revenuePerStation: figures.stations > 0 ? roundHalfUpBigInt(figures.netTakings, BigInt(figures.stations)) : null,
    };
}
/** `part ÷ whole` in basis points, half up; `null` when `whole` is not positive. */
export function basisPointsOf(part, whole) {
    if (whole <= 0)
        return null;
    return Number(roundHalfUpBigInt(BigInt(part) * 10000n, BigInt(whole)));
}
/** `current` against `base`: its direction and the change as basis points of the base. */
export function changeOf(current, base) {
    const difference = current - base;
    const direction = difference > 0n ? 'up' : difference < 0n ? 'down' : 'flat';
    if (base === 0n)
        return { direction, basisPoints: direction === 'flat' ? 0 : null };
    const magnitude = difference < 0n ? -difference : difference;
    const baseMagnitude = base < 0n ? -base : base;
    return { direction, basisPoints: Number(roundHalfUpBigInt(magnitude * 10000n, baseMagnitude)) };
}
/** `total ÷ days`, half away from zero to the unit (a money average lands on a whole minor unit, once). */
export function averageOf(total, days = DAILY_SUMMARY_TRAILING_DAYS) {
    return roundHalfUpBigInt(total, BigInt(days));
}
/**
 * A summed figure's trend: `lastWeek` is the same weekday a week before (`null` when it is not known), `week` the seven
 * business days before, oldest first (`null` when not known); the average is their sum ÷ 7, half up.
 */
export function trendOf(current, lastWeek, week) {
    const average = week === null ? null : averageOf(week.reduce((total, day) => total + day, 0n), DAILY_SUMMARY_TRAILING_DAYS);
    return {
        current,
        lastWeek,
        average,
        vsLastWeek: lastWeek === null ? null : changeOf(current, lastWeek),
        vsAverage: average === null ? null : changeOf(current, average),
    };
}
/** A ratio's trend (utilisation): each side in basis points; the average is the week's parts summed, then divided. */
export function ratioTrendOf(current, lastWeek, week) {
    const now = basisPointsOf(current.part, current.whole);
    const before = lastWeek === null ? null : basisPointsOf(lastWeek.part, lastWeek.whole);
    const average = week === null
        ? null
        : basisPointsOf(week.reduce((total, day) => total + day.part, 0), week.reduce((total, day) => total + day.whole, 0));
    return {
        current: now,
        lastWeek: before,
        average,
        vsLastWeek: now === null || before === null ? null : changeOf(BigInt(now), BigInt(before)),
        vsAverage: now === null || average === null ? null : changeOf(BigInt(now), BigInt(average)),
    };
}
/** Whether a day's refunds are well above usual: ≥ 10 % of its payments, or ≥ twice the trailing average. */
export function refundsHigh(refunds, payments, average) {
    if (refunds <= 0n)
        return false;
    if (payments > 0n && refunds * 10000n >= payments * REFUNDS_HIGH_SHARE_BPS)
        return true;
    return average !== null && average > 0n && refunds >= average * REFUNDS_HIGH_MULTIPLE;
}
/**
 * A venue's flags, in the order an owner reads them: the day not closed, the cash variance over the venue's alert
 * amount (an overage as much as a shortage, as N-23), refunds well above usual, a shift still open, stations offline a
 * long time, stock alerts.
 */
export function daySummaryFlagsOf(facts) {
    const flags = [];
    if (!facts.dayClosed)
        flags.push({ kind: 'day_not_closed', amount: null, count: null });
    const variance = facts.cashVariance < 0n ? -facts.cashVariance : facts.cashVariance;
    if (variance > facts.varianceAlertAmount)
        flags.push({ kind: 'cash_variance', amount: facts.cashVariance, count: null });
    if (refundsHigh(facts.refunds, facts.payments, facts.refundsAverage))
        flags.push({ kind: 'refunds_high', amount: facts.refunds, count: null });
    if (facts.openShifts > 0)
        flags.push({ kind: 'shift_open', amount: null, count: facts.openShifts });
    if (facts.stationsOfflineLong > 0)
        flags.push({ kind: 'station_offline', amount: null, count: facts.stationsOfflineLong });
    if (facts.stockAlerts !== null && facts.stockAlerts > 0)
        flags.push({ kind: 'stock_alerts', amount: null, count: facts.stockAlerts });
    return flags;
}
/**
 * How many stations were offline a long time in a business day: a stretch offline during a session of at least
 * `STATION_OFFLINE_LONG_MS`, counted inside the day's window and up to `now` for one not over.
 */
export function stationsOfflineLongOf(stretches, window, now) {
    const stations = new Set();
    for (const stretch of stretches) {
        const from = Math.max(stretch.from, window.from);
        const to = Math.min(stretch.to ?? now, window.to, now);
        if (to - from >= STATION_OFFLINE_LONG_MS)
            stations.add(stretch.stationId);
    }
    return stations.size;
}
/** The organisation's best venue: most net takings, above zero; ties to the earlier name. `null` with no takings. */
export function bestVenueOf(venues) {
    let best = null;
    for (const venue of venues) {
        if (venue.netTakings <= 0n)
            continue;
        if (best === null || venue.netTakings > best.netTakings || (venue.netTakings === best.netTakings && venue.name.localeCompare(best.name) < 0))
            best = venue;
    }
    return best?.venueId ?? null;
}
/**
 * The players of a window and the new ones among them (M-06, M-07), as sets — so an organisation's count across venues
 * is a union, not a sum. A player is new when their first session at the organisation started inside the window; one
 * whose first session is unknown is not new (the conservative reading, as `playerCountsOf`).
 */
export function playerSetsOf(sessionPlayers, firstSessionAt, window) {
    const players = new Set(sessionPlayers);
    const newPlayers = new Set();
    for (const player of players) {
        const first = firstSessionAt.get(player);
        if (first !== undefined && first >= window.from && first < window.to)
            newPlayers.add(player);
    }
    return { players, newPlayers };
}
/**
 * The copy key and the amount a flag is shown with. A cash variance reads "₹700.00 short" or "₹700.00 over" — the sign
 * picks the word and the amount is shown without one — never "off by -₹700.00" (owner review, 2026-10-07).
 */
export function dailySummaryFlagWording(flag) {
    if (flag.kind !== 'cash_variance' || flag.amount === null)
        return { key: flag.kind, amount: flag.amount };
    return {
        key: flag.amount.amount < 0 ? 'cash_variance_short' : 'cash_variance_over',
        amount: { ...flag.amount, amount: Math.abs(flag.amount.amount) },
    };
}
//# sourceMappingURL=daily-summary.js.map